// /admin/kitchen-flow — the owner's picture of the whole flow (Stage 3):
// [Catering orders] → Plans → Cooking → Store requests → Store → Buying.
// Each box counts what is late, short, waiting and on the way; tapping it
// lists what is behind the numbers, each linking to the screen that deals
// with it. Live on the requisition stream, with a slow poll behind it.
import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowDown, ArrowRight } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { useKitchenFlowGate } from '@/hooks/useKitchenFlowEnabled'
import { T } from '@/lib/inventoryFlow/strings'
import { FD } from '@/lib/inventoryFlow/flowDashboardStrings'
import { boxTone, deepLink, flagLabel, orderedNodes, sortItems, visibleCounts, type Tone } from '@/lib/inventoryFlow/flowDashboardLogic'
import { useRequisitionStream } from '@/lib/inventoryFlow/requisitionStream'
import type { FlowNode, FlowNodeKey } from '@/lib/inventoryFlow/visibilityTypes'
import { cn } from '@/lib/utils'
import { LiveDot } from './RequisitionList'

export function FlowDashboardPage() {
  const { enabled, isLoading, isError, retry } = useKitchenFlowGate()
  if (isLoading) return <div className="p-6 text-muted-foreground">{T.loading}</div>
  if (isError) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-muted-foreground">{T.settingsUnreachable}</p>
        <Button onClick={retry}>{T.retryNow}</Button>
      </div>
    )
  }
  if (!enabled) return <div className="p-6 text-muted-foreground">{T.notEnabled}</div>
  return <FlowDashboard />
}

const toneClass: Record<Tone, string> = {
  red: 'border-red-400 bg-red-50 dark:border-red-800 dark:bg-red-950/40',
  amber: 'border-amber-400 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40',
  green: 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30',
  idle: 'border-dashed bg-muted/40 text-muted-foreground',
}

const flagClass: Record<string, string> = {
  overdue: 'bg-red-600 text-white',
  short: 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100',
  waiting: 'bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100',
  in_transit: 'bg-sky-100 text-sky-900 dark:bg-sky-900/60 dark:text-sky-100',
}

function FlowDashboard() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<FlowNodeKey | null>(null)
  const refetch = useCallback(() => void qc.invalidateQueries({ queryKey: ['inventory-flow', 'flow-summary'] }), [qc])
  const live = useRequisitionStream({ enabled: true, onEvent: refetch, onReconnect: refetch })
  const q = useQuery({
    queryKey: ['inventory-flow', 'flow-summary'],
    queryFn: () => apiClient.getFlowSummary(),
    // The stream covers requests; plans, batches and POs change more slowly.
    refetchInterval: live === 'live' ? 120_000 : 60_000,
  })
  const s = q.data?.data
  const nodes = s ? orderedNodes(s) : []
  const current = nodes.find((n) => n.key === selected)?.node ?? null

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{FD.title}</h1>
          <p className="text-muted-foreground">{FD.subtitle}</p>
          {s && (
            <p className="text-sm text-muted-foreground">
              {FD.asOf(s.as_of)} · {FD.updated(new Date(q.dataUpdatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))}
            </p>
          )}
        </div>
        <LiveDot status={live} />
      </div>
      {q.isLoading && <p className="text-muted-foreground">{FD.loading}</p>}
      {q.isError && <p className="text-danger-fg">{FD.failed}</p>}

      <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-stretch">
        {nodes.map(({ key, node }, i) => (
          <div key={key} className="flex flex-col items-center gap-2 lg:flex-1 lg:flex-row">
            <NodeBox nodeKey={key} node={node} active={selected === key} onOpen={() => setSelected(selected === key ? null : key)} />
            {i < nodes.length - 1 && (
              <>
                <ArrowDown className="h-4 w-4 shrink-0 text-muted-foreground lg:hidden" aria-hidden />
                <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground lg:block" aria-hidden />
              </>
            )}
          </div>
        ))}
      </div>

      {selected && current && <NodeList nodeKey={selected} node={current} onClose={() => setSelected(null)} />}
    </div>
  )
}

function NodeBox({ nodeKey, node, active, onOpen }: { nodeKey: FlowNodeKey; node: FlowNode | null; active: boolean; onOpen: () => void }) {
  // A node the server did not send: drawn, marked, and inert.
  if (!node) {
    return (
      <div className={cn('flex min-h-[8.5rem] w-full flex-col justify-between rounded-lg border-2 p-4', toneClass.idle)} data-slot={nodeKey}>
        <div>
          <p className="text-base font-semibold">{FD.nodes[nodeKey]}</p>
          <p className="text-sm">{FD.hints[nodeKey]}</p>
        </div>
        <p className="text-sm font-medium">{FD.comingSoon}</p>
      </div>
    )
  }
  const tone = boxTone(node)
  const counts = visibleCounts(nodeKey, node.counts)
  return (
    <button type="button" onClick={onOpen} aria-expanded={active}
      className={cn('flex min-h-[8.5rem] w-full flex-col justify-between rounded-lg border-2 p-4 text-left touch-manipulation transition-shadow',
        toneClass[tone], active && 'ring-2 ring-primary ring-offset-2')}>
      <div>
        <p className="text-base font-semibold">{FD.nodes[nodeKey]}</p>
        <p className="text-sm text-muted-foreground">{FD.hints[nodeKey]}</p>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {counts.length === 0 ? (
          <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">{FD.allClear}</span>
        ) : counts.map((c) => (
          <span key={c.flag} className={cn('rounded-full px-2.5 py-1 text-sm font-semibold tabular-nums', flagClass[c.flag])}>
            {c.n} {c.label}
          </span>
        ))}
      </div>
    </button>
  )
}

function NodeList({ nodeKey, node, onClose }: { nodeKey: FlowNodeKey; node: FlowNode; onClose: () => void }) {
  const items = sortItems(node.items)
  const total = node.counts.waiting + node.counts.short + node.counts.in_transit + node.counts.overdue
  const screen = deepLink(nodeKey)
  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{FD.nodes[nodeKey]}</h2>
        <div className="flex gap-2">
          <Button asChild className="px-4">
            <Link to={screen.to} search={screen.search as never}>{FD.openScreen}</Link>
          </Button>
          <Button variant="ghost" className="px-4" onClick={onClose}>{FD.close}</Button>
        </div>
      </div>
      {items.length === 0 ? <p className="text-muted-foreground">{FD.nothing}</p> : (
        <ul className="divide-y rounded-lg border">
          {items.map((it) => {
            const link = deepLink(nodeKey, it)
            return (
              <li key={`${it.flag}-${it.id}`}>
                <Link to={link.to} search={link.search as never} className="flex min-h-[3.5rem] flex-wrap items-center justify-between gap-2 px-3 py-2 hover:bg-muted">
                  <span>
                    <span className="font-medium">{it.ref}</span>
                    {it.label && it.label !== it.ref && <span className="text-muted-foreground"> · {it.label}</span>}
                  </span>
                  <span className="flex items-center gap-2 text-sm">
                    {it.date && <span className="text-muted-foreground">{it.date}</span>}
                    <span className={cn('rounded-full px-2 py-0.5 font-semibold', flagClass[it.flag])}>{flagLabel(nodeKey, it.flag)}</span>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
      {total > items.length && <p className="text-sm text-muted-foreground">{FD.more(total - items.length)}</p>}
    </section>
  )
}
